/**
 * CSV Export Utilities
 * 
 * Utility functions for exporting visiting card data to CSV format.
 * Handles array fields, special characters, and proper CSV formatting.
 */

/**
 * Format array field for CSV display
 * Joins array elements with semicolons, returns 'N/A' for empty arrays
 * @param {Array} array - Array of values to format
 * @returns {string} Formatted string
 */
export const formatArrayField = (array) => {
  if (!array || !Array.isArray(array) || array.length === 0) {
    return 'N/A';
  }
  return array.filter(item => item).join('; ');
};

/**
 * Escape CSV field value
 * Wraps values containing commas, quotes, or newlines in quotes
 * Escapes internal quotes by doubling them
 * @param {*} value - Value to escape
 * @returns {string} Escaped CSV field
 */
export const escapeCSVField = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  
  const stringValue = String(value);
  
  // Replace newlines with spaces
  const cleanedValue = stringValue.replace(/\n/g, ' ').replace(/\r/g, '');
  
  // If value contains comma, quote, or was multiline, wrap in quotes
  if (cleanedValue.includes(',') || cleanedValue.includes('"') || stringValue.includes('\n')) {
    return `"${cleanedValue.replace(/"/g, '""')}"`;
  }
  
  return cleanedValue;
};

/**
 * Convert visiting cards array to CSV string
 * @param {Array} cards - Array of card objects
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (cards) => {
  if (!cards || cards.length === 0) {
    return '';
  }

  // Define CSV headers
  const headers = [
    'Card ID',
    'Name',
    'Role',
    'Company',
    'Phone',
    'Email',
    'Address',
    'Website',
    'Source Images',
    'Batch ID',
    'Timestamp'
  ];

  // Create header row
  const headerRow = headers.join(',');

  // Create data rows
  const dataRows = cards.map(card => {
    const row = [
      escapeCSVField(card.cardid || 'N/A'),
      escapeCSVField(card.name || 'N/A'),
      escapeCSVField(card.role || 'N/A'),
      escapeCSVField(card.company || 'N/A'),
      escapeCSVField(formatArrayField(card.phone)),
      escapeCSVField(formatArrayField(card.email)),
      escapeCSVField(card.address || 'N/A'),
      escapeCSVField(formatArrayField(card.website)),
      escapeCSVField(formatArrayField(card.sourceImages)),
      escapeCSVField(card.batchId || 'N/A'),
      escapeCSVField(card.timestamp || 'N/A')
    ];
    return row.join(',');
  });

  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n');
};

/**
 * Trigger CSV file download
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Desired filename (without extension)
 */
export const downloadCSV = (csvContent, filename = 'visiting_cards') => {
  // Create blob with CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export visiting cards to CSV file
 * Main export function that combines conversion and download
 * @param {Array} cards - Array of card objects
 * @param {string} filename - Optional custom filename
 */
export const exportToCSV = (cards, filename) => {
  if (!cards || cards.length === 0) {
    console.warn('No cards to export');
    return;
  }

  // Generate filename with timestamp if not provided
  const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const finalFilename = filename || `visiting_cards_export_${timestamp}`;

  // Convert to CSV and download
  const csvContent = convertToCSV(cards);
  downloadCSV(csvContent, finalFilename);
};

/**
 * Generate a unique batch ID
 * @returns {string} Unique batch identifier
 */
export const generateBatchId = () => {
  return `batch_${Date.now()}`;
};

/**
 * Format timestamp for display
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Formatted date/time string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return timestamp;
  }
};

